import * as React from 'react'
import { connect } from 'react-redux'
import { Form, Formik, FormikProps } from 'formik'
import { RouteComponentProps } from 'react-router'

import * as Yup from 'yup'
import classnames from 'classnames'

import Moment from 'react-moment'
import { Tooltip } from 'react-tippy'

import { Input, Label, Button, FormGroup } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { RootState } from '../../store/rootReducer'
import { Claim } from '../../lib/interfaces/Claim.interface'
import { Vendor } from '../../lib/interfaces/Vendor.interface'
import { AccidentType } from '../../lib/interfaces/AccidentType.interface'
import { AccidentStatus } from '../../lib/interfaces/AccidentStatus.interface'

import Breadcrumbs from '../Breadcrumbs.component'
import TextArea from '../../common/TextArea.component'
import InputCard from '../../common/InputCard.component'
import CustomSelect from '../../common/CustomSelect.component'
import DatePickerCard from '../../common/DatePickerCard.component'

import * as appActions from '../../store/app/actions'
import * as accidentsActions from '../../store/accidents/actions'

const get = require('lodash/get')
const map = require('lodash/map')
const slice = require('lodash/slice')
const isEmpty = require('lodash/isEmpty')
const debounce = require('lodash/debounce')

const moment = require('moment')

interface RouteInfo {
  claimId?: string
  accidentId: string
}

export interface ClaimCreationPageProps extends RouteComponentProps<RouteInfo> {
  claim?: Claim
  vendors?: Vendor[]
  fetchVendors: () => void
  isSearchingClaims: boolean
  claimsSearchError?: string
  claimTypes?: AccidentType[]
  claimSearchResults: Claim[]
  fetchClaimTypes: () => void
  fetchClaimStatus: () => void
  claimStatus?: AccidentStatus[]
  searchClaims: (params: any) => void
  upsertClaim: (claim: Claim) => void
  fetchClaim: (claimId: number) => void
}

interface ClaimCreationPageState {
  vinTooltipOpen: boolean
  pageStatus: 'loading' | 'done'
}

class ClaimCreationPage extends React.PureComponent<ClaimCreationPageProps, ClaimCreationPageState> {
  vin?: string
  resetForm?: (value: any) => void = undefined

  debounceVINSearch = debounce(() => {
    const { searchClaims } = this.props

    if (!isEmpty(this.vin)) {
      searchClaims({ vin: this.vin })
    }
  }, 300)

  constructor(props) {
    super(props)

    this.onSubmit = this.onSubmit.bind(this)

    this.state = {
      vinTooltipOpen: false,
      pageStatus: this.claimId  ? 'loading' : 'done'
    }
  }

  componentDidMount() {
    const { fetchClaim, fetchVendors, fetchClaimTypes, fetchClaimStatus } = this.props

    if (this.claimId) {
      fetchClaim(this.claimId)
    }

    fetchVendors()
    fetchClaimTypes()
    fetchClaimStatus()
  }

  componentDidUpdate(prevProps, prevState) {
    const { claim, fetchClaim } = this.props

    if (this.claimId !== get(prevProps, 'match.params.claimId')) {
      this.setState({ pageStatus: 'loading' }, () => fetchClaim(this.claimId))
    }
    else if (claim && !prevProps.claim) {
      this.setState({ pageStatus: 'done' })
    }
  }

  componentWillUnmount() {
    if (this.resetForm) {
      this.resetForm({})
    }
  }

  get claimId() {
    return get(this.props.match, 'params.claimId')
  }

  get accidentId() {
    return get(this.props.match, 'params.accidentId')
  }

  get validationSchema() {
    return Yup.object().shape({
      damage: Yup.number().required(),
      vendorId: Yup.number().required(),
      vehicleVin: Yup.string().required(),
      claimTypeId: Yup.number().required(),
      vehicleMake: Yup.string().required(),
      vehicleModel: Yup.string().required(),
      claimStatusId: Yup.number().required()
    })
  }

  get initialValues() {
    const { claim } = this.props
    const { pageStatus } = this.state

    if (pageStatus !== 'done') { return null }

    if (!this.claimId || !claim) {
      return {
        damage: 0,
        accidentId: this.accidentId
      }
    }

    const vehicleYear = claim.vehicleYear && moment(claim.vehicleYear).format('YYYY')

    return {
      id: claim.id,
      damage: claim.damage || 0,
      accidentId: this.accidentId,
      vehicleYear: vehicleYear || '',
      adminFees: claim.adminFees || '',
      lossOfUse: claim.lossOfUse || '',
      vendorId: get(claim, 'vendor.id'),
      vehicleVin: claim.vehicleVin || '',
      description: claim.description || '',
      vehicleMake: claim.vehicleMake || '',
      vehicleModel: claim.vehicleModel || '',
      enterpriseId: claim.enterpriseId || '',
      employeeId: get(claim, 'employee.id', ''),
      insuranceClaim: claim.insuranceClaim || '',
      claimTypeId: get(claim, 'claimType.id', ''),
      claimStatusId: get(claim, 'claimStatus.id', ''),
      invoiceReceived: claim.invoiceReceived && new Date(claim.invoiceReceived)
    }
  }

  get vehicleYearOptions() {
    const thisYear = moment().startOf('year')

    const vehicleYearOptions: any = []

    const nextYear = `${thisYear.year() + 1}`
    vehicleYearOptions.push({ value: nextYear, label: nextYear })
    vehicleYearOptions.push({ value: `${thisYear.year()}`, label: `${thisYear.year()}` })

    for (let i = 1; i <= 16; i++) {
      const year = `${thisYear.year() - i}`
      vehicleYearOptions.push({ value: year, label: year })
    }

    return vehicleYearOptions
  }

  onChangeVINNumber = (e, formProps) => {
    this.vin = e.target.value

    this.setState({ vinTooltipOpen: false }, () => {
      formProps.setFieldValue('vehicleVin', this.vin)
      this.debounceVINSearch()
    })
  }

  onSubmit(values) {
    const { upsertClaim } = this.props

    upsertClaim(values as Claim)
  }

  renderVINTooltipContent() {
    const { claimSearchResults, claimsSearchError } = this.props

    const hasMore = get(claimSearchResults, 'length', 0) > 5
    const first5Claims = slice(claimSearchResults, 0, 5)

    if (claimsSearchError) {
      return (
        <div className="ggmouseover d-flex flex-column align-items-center p-3">
          <h6>{claimsSearchError}</h6>
        </div>
      )
    }

    return (
      <div className="ggmouseover d-flex flex-column align-items-center p-3">
        <h6><b>Claims</b></h6>
        {map(first5Claims, claim => (
          <a
            key={claim.id}
            target="_blank"
            style={{ color: 'white' }}
            href={`/accidents/${get(claim, 'accident.id')}`}
          >
            {`ERAC: ${get(claim, 'enterpriseId', 'None')}`}
            {/* {' - '}
            <Moment date={claim.lossDate} format="MM.DD.YYYY" /> */}
          </a>
        ))}

        {hasMore &&
          <span className="text-small mt-2">{`and ${claimSearchResults.length - 5} more`}</span>
        }
      </div>
    )
  }

  renderVINNumberInput(formProps) {
    const { vinTooltipOpen } = this.state
    const { isSearchingClaims, claimSearchResults, claimsSearchError } = this.props

    const hasClaims = !isSearchingClaims && get(claimSearchResults, 'length', 0) > 0

    let rightAddonProps
    if (isSearchingClaims) {
      rightAddonProps = { spin: true, icon: ['fas', 'spinner'] }
    }
    else if (hasClaims || claimsSearchError) {
      rightAddonProps = { icon: ['fas', 'exclamation-circle'] }
    }
    else {
      rightAddonProps = { icon: ['fas', 'check-circle' ] }
    }

    const renderTooltipWrapper = children => (
      <Tooltip
        trigger="click"
        interactive={true}
        open={vinTooltipOpen}
        html={this.renderVINTooltipContent()}
        onRequestClose={() => this.setState({ vinTooltipOpen: false })}
      >
        {children}
      </Tooltip>
    )

    const renderVINSearchIcon = () => (
      <FontAwesomeIcon
        {...rightAddonProps}
        style={{ cursor: hasClaims || claimsSearchError ? 'pointer' : 'default '}}
        onClick={e => {
          e.stopPropagation()
          if (hasClaims || claimsSearchError) {
            this.setState({ vinTooltipOpen: !vinTooltipOpen })
          }
        }}
      />
    )

    return (
      <InputCard
        placeholder="Enter vehicle VIN"
        value={formProps.values.vehicleVin}
        error={!!formProps.errors.vehicleVin}
        onChange={e => this.onChangeVINNumber(e, formProps)}
        rightAddon={formProps.values.vehicleVin
          ? (
            hasClaims || claimsSearchError
            ? renderTooltipWrapper(renderVINSearchIcon())
            : renderVINSearchIcon()
          )
          : null
        }
      />
    )
  }

  renderVehicleYearField(formProps) {
    return (
      <CustomSelect
        type="normal"
        style={{ width: '100%' }}
        placeholder="Select vehicle year"
        options={this.vehicleYearOptions}
        getOptionLabel={year => year.label}
        getOptionValue={year => year.value}
        value={formProps.values.vehicleYear}
        valueContainerStyle={{ textAlign: 'left' }}
        placeholderStyle={{ fontWeight: 'light', fontStyle: 'italic' }}
        onChange={value => formProps.setFieldValue('vehicleYear', value)}
      />
    )
  }

  renderForm() {
    const { vendors, claimTypes, claimStatus } = this.props

    return (
      <Formik initialValues={this.initialValues} onSubmit={this.onSubmit} validationSchema={this.validationSchema}>
        {formProps => {
          this.resetForm = formProps.handleReset

          return (
            <Form>
              <section>
                <h5 className="title">{`${this.claimId ? 'Edit claim' : 'Create new claim'}: Basic details`}</h5>

                <div className="row no-gutters mt-3">
                  <div className="col-12 col-md-6 col-lg-3 mt-3 mt-md-0 pr-md-3">
                    <InputCard
                      name="enterpriseId"
                      placeholder="Enterprise ID"
                      onChange={formProps.handleChange}
                      value={formProps.values.enterpriseId}
                      error={!!formProps.errors.enterpriseId}
                      className={classnames({ 'ggcard--error': !!formProps.errors.enterpriseId })}
                    />
                  </div>

                  <div className="col-12 col-md-6 col-lg-3 mt-3 mt-md-0 pr-lg-3">
                    <InputCard
                      name="employeeId"
                      placeholder="Driver ID"
                      onChange={formProps.handleChange}
                      value={formProps.values.employeeId}
                      error={!!formProps.errors.employeeId}
                      className={classnames({ 'ggcard--error': !!formProps.errors.employeeId })}
                    />
                  </div>

                  <div className="col-12 col-md-6 col-lg-3 pr-md-3 mt-3 mt-lg-0">
                    <div style={{ overflow: 'visible' }} className={classnames('ggcard p-2', { 'ggcard--error': !!formProps.errors.claimTypeId })}>
                      <CustomSelect
                        type="normal"
                        options={claimTypes}
                        style={{ width: '100%' }}
                        getOptionValue={type => type.id}
                        placeholder="Select a claim type"
                        getOptionLabel={type => type.name}
                        value={formProps.values.claimTypeId}
                        valueContainerStyle={{ textAlign: 'left' }}
                        placeholderStyle={{ fontWeight: 'light', fontStyle: 'italic' }}
                        onChange={value => formProps.setFieldValue('claimTypeId', value)}
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-6 col-lg-3 mt-3 mt-lg-0">
                    <div style={{ overflow: 'visible' }} className={classnames('ggcard p-2', { 'ggcard--error': !!formProps.errors.claimStatusId })}>
                      <CustomSelect
                        type="normal"
                        options={claimStatus}
                        style={{ width: '100%' }}
                        placeholder="Select a claim status"
                        getOptionValue={status => status.id}
                        value={formProps.values.claimStatusId}
                        getOptionLabel={status => status.name}
                        valueContainerStyle={{ textAlign: 'left' }}
                        placeholderStyle={{ fontWeight: 'light', fontStyle: 'italic' }}
                        onChange={value => formProps.setFieldValue('claimStatusId', value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="row no-gutters mt-3">
                  <div className="col-12 col-lg-8 mt-3 mt-lg-0">
                    <div className="ggcard h-100 w-100 p-3">
                      <h6 className="title">Claim description</h6>

                      <TextArea type="grey" className="h-100 mt-2" name="description" onChange={formProps.handleChange} value={formProps.values.description} maxLength={300} />
                    </div>
                  </div>

                  <div className="col-12 col-lg-4 pl-lg-3">
                    <DatePickerCard
                      title="Invoice received date"
                      value={formProps.values.invoiceReceived}
                      onChange={value => formProps.setFieldValue('invoiceReceived', value)}
                      cardClassName={classnames({ 'ggcard--error': !!formProps.errors.invoiceReceived })}
                    />
                  </div>
                </div>
              </section>

              <section className="mt-5">
                <h5 className="title">Vehicle details</h5>

                <div className="row no-gutters mt-3">
                  <div className="col-12 col-md-6 col-lg-3 mt-lg-0 pr-md-3">
                    <InputCard
                      name="vehicleMake"
                      placeholder="Enter vehicle make"
                      onChange={formProps.handleChange}
                      value={formProps.values.vehicleMake}
                      error={!!formProps.errors.vehicleMake}
                    />
                  </div>
                  <div className="col-12 col-md-6 col-lg-3 mt-3 mt-md-0 pr-lg-3">
                    <InputCard
                      name="vehicleModel"
                      placeholder="Enter vehicle model"
                      onChange={formProps.handleChange}
                      value={formProps.values.vehicleModel}
                      error={!!formProps.errors.vehicleModel}
                    />
                  </div>
                  <div className="col-12 col-md-6 col-lg-3 mt-3 mt-lg-0 pr-md-3">
                    {this.renderVINNumberInput(formProps)}
                  </div>
                  <div className="col-12 col-md-6 col-lg-3 mt-3 mt-lg-0">
                    <div style={{ overflow: 'visible' }} className={classnames('ggcard p-2', { 'ggcard--error': !!formProps.errors.vehicleYear })}>
                      {this.renderVehicleYearField(formProps)}
                    </div>
                  </div>

                  <div className="col-12 col-md-6 col-lg-3 mt-4 pr-lg-3">
                    <FormGroup>
                      <Label className="font-weight-bold text-secondary">Insurance Claim #</Label>
                      <InputCard
                        name="insuranceClaim"
                        placeholder="Insurance claim #"
                        onChange={formProps.handleChange}
                        value={formProps.values.insuranceClaim}
                        error={!!formProps.errors.insuranceClaim}
                      />
                    </FormGroup>
                  </div>
                </div>
              </section>

              <section className="mt-4">
                <h5 className="title">Vendor</h5>

                <div className="row no-gutters mt-3">
                  <div className="col-12 col-md-6 col-lg-3 mt-3 mt-lg-0 pr-md-3">
                    <div style={{ overflow: 'visible' }} className={classnames('ggcard p-2', { 'ggcard--error': !!formProps.errors.vendorId })}>
                      <CustomSelect
                        type="normal"
                        options={vendors}
                        style={{ width: '100%' }}
                        placeholder="Select vendor"
                        value={formProps.values.vendorId}
                        getOptionValue={vendor => vendor.id}
                        getOptionLabel={vendor => vendor.name}
                        valueContainerStyle={{ textAlign: 'left' }}
                        onChange={value => formProps.setFieldValue('vendorId', value)}
                        placeholderStyle={{ fontWeight: 'light', fontStyle: 'italic' }}
                      />
                    </div>
                  </div>
                </div>
              </section>

              <section className="mt-5">
                <h5 className="title">Fees</h5>

                <div className="row no-gutters mt-3">
                  <div className="col-12 col-md-6 col-lg-3 pr-md-3">
                    <FormGroup>
                      <Label className="font-weight-bold text-secondary">Damage</Label>
                      <InputCard
                        currency
                        value={formProps.values.damage}
                        error={!!formProps.errors.damage}
                        placeholder="Enter damage amount"
                        onChange={value => formProps.setFieldValue('damage', value)}
                      />
                    </FormGroup>
                  </div>
                </div>
              </section>

              <div className="row no-gutters mt-5 mb-3">
                <div className="col-12">
                  <Button color="primary" className="w-100" disabled={!formProps.isValid}>
                    Save claim
                  </Button>
                </div>
              </div>
            </Form>
          )
        }}
      </Formik>
    )
  }

  render() {
    const { pageStatus } = this.state

    return (
      <div>
        <Breadcrumbs breadcrumbs={[
          { name: 'Accidents', link: '/accidents' },
          { name: `${this.accidentId}`, link: `/accidents/${this.accidentId}` },
          { name: 'Add claim', active: true }
        ]} />

        {pageStatus === 'loading'
          ? <div className="p-3">Loading...</div>
          : (
            <div className="page claim-page container-fluid">
              {this.renderForm()}
            </div>
          )
        }
      </div>
    )
  }
}

const mapStateToProps = (state: RootState) => ({
  vendors: state.app.vendors,
  claim: state.accidents.claimDetails,
  claimTypes: state.accidents.accidentTypes,
  claimStatus: state.accidents.accidentStatus,
  claimsSearchError: state.accidents.claimsSearchError,
  isSearchingClaims: state.accidents.isSearchingClaims,
  claimSearchResults: state.accidents.claimSearchResults,
})

const mapDispatchToProps = (dispatch) => ({
  fetchVendors: () => dispatch(appActions.fetchVendors()),
  searchClaims: (params: any) => dispatch(accidentsActions.searchClaims(params)),
  upsertClaim: (claim: Claim) => dispatch(accidentsActions.upsertClaim(claim)),
  fetchClaim: (claimId: number) => dispatch(accidentsActions.fetchClaim(claimId)),
  fetchClaimTypes: () => dispatch(accidentsActions.fetchAccidentTypesDispatchCommand()),
  fetchClaimStatus: () => dispatch(accidentsActions.fetchAccidentStatusDispatchCommand())
})

export default connect(mapStateToProps, mapDispatchToProps)(ClaimCreationPage)



// WEBPACK FOOTER //
// ./src/routes/accidents/ClaimCreation.page.tsx